import { inspect } from 'util';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import Table from 'cli-table';
import descriptions from '../../helpers/descriptions.js';
import { pr, pp, cl, themed, printJSON } from '../../helpers/print.js';
import { getScheduleList, updateSchedule } from '../../helpers/api.js';

const debug = Debug('commands:schedules:bulkUpdate');

const command = 'bulk-update';

const desc = 'Update schedules in bulk';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('tenant-ids', {
      describe: 'Comma-separated list of tenant IDs',
      type: 'string',
      alias: ['tenants'],
    })
    .option('flow-ids', {
      describe: 'Comma-separated list of flow IDs',
      type: 'string',
      alias: ['flows'],
    })
    .option('status', {
      describe: 'Schedule status: "enable" or "disable"',
      type: 'string',
      choices: ['enable', 'disable'],
    })
    .option('expression', {
      describe: 'Cron schedule expression',
      type: 'string',
    })
    .option('connector', {
      describe: 'Filter by connector ID',
      type: 'string',
    })
    .option('job-type', {
      describe: 'Filter by job type: "read" or "write"',
      type: 'string',
      choices: ['read', 'write'],
    });
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, tenantIds, flowIds, status, expression, connector, 'job-type': jobType } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  
  try {
    // Validate that either status or expression is provided
    if (!status && !expression) {
      const errorMsg = 'Either "status" or "expression" must be provided';
      if (json) {
        printJSON({ status: 'error', error: errorMsg });
      } else {
        spinner.fail(themed(`Error: ${errorMsg}`, 'secondary'));
      }
      process.exit(1);
    }

    // Parse comma-separated lists
    const tenantList = tenantIds ? tenantIds.split(',').map(t => t.trim()).filter(Boolean) : null;
    const flowList = flowIds ? flowIds.split(',').map(f => f.trim()).filter(Boolean) : null;

    // 1. Get all schedules
    message = themed(`Retrieving schedules for environment: ${themed(env, 'info')}`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    
    const allSchedules = await getScheduleList({
      debug,
      baseUri: clientApiBaseUri,
      env,
      apikey,
      includeDisabled: true, // Get both enabled and disabled schedules
    });
    
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    debug('response-data', allSchedules);

    // Parse schedules if they come as a JSON string
    const schedules = typeof allSchedules === 'string' ? JSON.parse(allSchedules) : allSchedules;

    if (!schedules || schedules.length === 0) {
      if (json) {
        printJSON({ updated: 0, schedules: [] });
      } else {
        spinner.warn(themed(`Warning: ${themed('No schedules found for the specified environment')}.`, 'secondary'));
      }
      return;
    }

    // 2. Filter schedules based on criteria
    let filteredSchedules = schedules.filter(schedule => {
      // Filter by tenants
      if (tenantList && tenantList.length > 0) {
        if (!tenantList.includes(schedule.tenantId)) {
          return false;
        }
      }

      // Filter by flows
      if (flowList && flowList.length > 0) {
        if (!flowList.includes(schedule.flowId)) {
          return false;
        }
      }

      // Filter by connector
      if (connector !== undefined && connector !== null && connector !== '') {
        // Filter by specific connector_id
        if (schedule.connectorId !== connector) {
          return false;
        }
      } else {
        // If connector not passed, get only schedules with connectorId equals to null
        if (schedule.connectorId !== null && schedule.connectorId !== undefined) {
          return false;
        }
      }

      // Filter by job_type
      if (jobType) {
        if (schedule.jobType !== jobType) {
          return false;
        }
      }

      return true;
    });

    if (filteredSchedules.length === 0) {
      if (json) {
        printJSON({ updated: 0, schedules: [] });
      } else {
        spinner.warn(themed(`Warning: ${themed('No schedules match the specified filters')}.`, 'secondary'));
      }
      return;
    }

    // 3. Filter schedules based on status to avoid unnecessary updates
    // Only enable disabled schedules, only disable enabled schedules
    let schedulesToUpdate = filteredSchedules;
    if (status) {
      if (status === 'enable') {
        // Only update schedules that are currently disabled
        schedulesToUpdate = filteredSchedules.filter(schedule => schedule.isActive === false);
      } else if (status === 'disable') {
        // Only update schedules that are currently enabled
        schedulesToUpdate = filteredSchedules.filter(schedule => schedule.isActive === true);
      }
    }

    if (schedulesToUpdate.length === 0) {
      if (json) {
        printJSON({ updated: 0, schedules: [], message: 'No schedules need to be updated (all are already in the desired state)' });
      } else {
        spinner.warn(themed(`Warning: ${themed('No schedules need to be updated (all are already in the desired state)')}.`, 'secondary'));
      }
      return;
    }

    // 4. Prepare update payload
    const state = status ? (status === 'enable' ? 'ENABLED' : 'DISABLED') : undefined;

    // 5. Print how many schedules will be updated
    if (!json) {
      pr(themed(`Found ${themed(schedulesToUpdate.length.toString(), 'info')} schedule(s) to update`, 'secondary'));
    }

    // 6. Update schedules in batches of 20
    const BATCH_SIZE = 20;
    const updatedSchedules = [];
    const failedSchedules = [];

    for (let i = 0; i < schedulesToUpdate.length; i += BATCH_SIZE) {
      const batch = schedulesToUpdate.slice(i, i + BATCH_SIZE);
      const batchNum = Math.floor(i / BATCH_SIZE) + 1;
      const totalBatches = Math.ceil(schedulesToUpdate.length / BATCH_SIZE);

      message = themed(`Updating batch ${themed(`${batchNum}/${totalBatches}`, 'info')} (${batch.length} schedule(s))`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

      const updatePromises = batch.map(async (schedule) => {
        try {
          // Use provided expression or keep the existing one from the schedule
          const scheduleExpression = expression || schedule.expression;
          
          // Use provided state or keep the existing one from the schedule
          const scheduleState = state || (schedule.isActive ? 'ENABLED' : 'DISABLED');
          
          // Ensure we have an expression (required by the endpoint)
          if (!scheduleExpression) {
            throw new Error('Schedule expression is required but not found');
          }

          const result = await updateSchedule({
            debug,
            baseUri: clientApiBaseUri,
            env: schedule.envId,
            flow: schedule.flowId,
            user_id: schedule.tenantId,
            apikey,
            schedule_expression: scheduleExpression,
            state: scheduleState,
            job_type: schedule.jobType || jobType,
            connector_id: schedule.connectorId || connector,
          });

          return {
            success: true,
            schedule: {
              envId: schedule.envId,
              flowId: schedule.flowId,
              tenantId: schedule.tenantId,
              connectorId: schedule.connectorId,
              jobType: schedule.jobType,
              expression: scheduleExpression,
              state: scheduleState,
            },
            result,
          };
        } catch (err) {
          debug('Error updating schedule:', err);
          return {
            success: false,
            schedule: {
              envId: schedule.envId,
              flowId: schedule.flowId,
              tenantId: schedule.tenantId,
              connectorId: schedule.connectorId,
              jobType: schedule.jobType,
            },
            error: err.message || err.toString(),
          };
        }
      });

      const batchResults = await Promise.all(updatePromises);
      
      batchResults.forEach(result => {
        if (result.success) {
          updatedSchedules.push(result.schedule);
        } else {
          failedSchedules.push(result);
        }
      });

      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    }

    // 7. Print results
    if (json) {
      printJSON({
        total: schedulesToUpdate.length,
        updated: updatedSchedules.length,
        failed: failedSchedules.length,
        schedules: updatedSchedules,
        errors: failedSchedules,
      });
    } else {
      pr(themed(`\nUpdated ${themed(updatedSchedules.length.toString(), 'info')} schedule(s) successfully`, 'secondary'));
      
      if (failedSchedules.length > 0) {
        pr(themed(`Failed to update ${themed(failedSchedules.length.toString(), 'error')} schedule(s)`, 'secondary'));
      }

      if (updatedSchedules.length > 0 || failedSchedules.length > 0) {
        const table = new Table({
          head: ['Environment', 'Flow', 'Tenant', 'Connector', 'Job Type', 'Expression', 'State', 'Updated'],
        });
        
        // Add successful schedules
        updatedSchedules.forEach((schedule) => {
          table.push([
            schedule.envId,
            schedule.flowId,
            schedule.tenantId,
            schedule.connectorId || 'N/A',
            schedule.jobType || 'N/A',
            schedule.expression,
            schedule.state,
            'yes',
          ]);
        });
        
        // Add failed schedules
        failedSchedules.forEach((failed) => {
          const schedule = failed.schedule;
          // Find the original schedule to get expression and state if available
          const originalSchedule = schedulesToUpdate.find(
            s => s.envId === schedule.envId && 
                 s.flowId === schedule.flowId && 
                 s.tenantId === schedule.tenantId &&
                 s.connectorId === schedule.connectorId &&
                 s.jobType === schedule.jobType
          );
          
          table.push([
            schedule.envId,
            schedule.flowId,
            schedule.tenantId,
            schedule.connectorId || 'N/A',
            schedule.jobType || 'N/A',
            originalSchedule?.expression || 'N/A',
            originalSchedule?.isActive ? 'ENABLED' : 'DISABLED',
            `no: ${failed.error}`,
          ]);
        });
        
        console.log('\n' + table.toString());
      }
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err.message || err.toString() });
    } else {
      spinner.fail(themed(`Error: ${message || 'Failed to update schedules'}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message || err.toString())}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
